import * as React from 'react';
import { useEffect } from "react";
import TimerManager from "../../services/TimerManager";
import Validator from "../../services/Validator/HealthCard/Validator";
import RegionCodeValidator from "../../services/Validator/RegionCode/RegionCodeValidator";
import { ACQUIRING_MODE } from '../../store/AcquireHealthCardNumber/type';
import { Scanner } from "./Scanner";
import { IAcquireHealthCardNumberProps } from "./Types";
import { ZipCodeConfirmation } from "./ZipCodeConfirmation";
import { Loader } from '../genericComponents/Loader/Loader';
import { HealthCardAcquisitionType, ClientEventScreen, InteractionType } from '../../dtos/KioskDtos.dtos';
import { ErrorCodes } from '../../dtos/KioskDtos.dtos';
import Client from '../../services/Client';
import { addDebugMessage } from '../../store/Utilities/actions';
import { ManualInput } from './ManualInput';

function LoadingSpinner() {
    return (
        <div id="acquiringHealthCardLoadingSpinner" className="text-center">
            <Loader />
        </div>
    );
}

export const AcquireHealthCardNumber = (props: IAcquireHealthCardNumberProps) => {
    const MAXIMUM_NUMBER_ATTEMPTS = 1;
    useEffect(() => {
        TimerManager.setCurrentScreen(ClientEventScreen.AcquireHealthCardManualMode)
        props.setupEnterFunction(() => submitHealthCardNumberToServer(props.healthCardNumber, HealthCardAcquisitionType.ManualEntry));
        return () => props.setupEnterFunction(() => { });
    }, []);

    useEffect(() => {
        if (!props.healthCardManualEntryOnlyEnabled && props.acquiringMethod === ACQUIRING_MODE.MANUAL) {
            TimerManager.resetInactivityListener();
        } else {
            TimerManager.stopInactivityListener();
        }
    }, [props.acquiringMethod, props.healthCardManualEntryOnlyEnabled]);

    const submitHealthCardNumberToServer = (healthCardNumber: string, acquisitionType: HealthCardAcquisitionType) => {
        if (acquisitionType === HealthCardAcquisitionType.BarCodeScanner) {
            addDebugMessage("Scanned Barcode Content", false, { barcodeScanContent: healthCardNumber });
            for (let parsers of props.healthCardParsers) {
                const result = parsers.Parse(healthCardNumber);
                if (result.hasResult && result.healthCardNumber) {
                    props.submitHealthCardNumberToServer(result.healthCardNumber, acquisitionType)
                    return;
                }
            }
            props.showAndUpdateErrorMessage(ErrorCodes.BarcodeUnreadable);
        } else {
            props.submitHealthCardNumberToServer(healthCardNumber, acquisitionType);
        }
    }

    const onZipCodeConfirmationSubmit = () => {
        if (props.regionCodesFromServer.includes(props.postalCodeEntered.replace(/\s|\(|\)|_|-|\+/g, ''))) {
            props.postalCodeAuthenticatedDispatch();
        } else if (props.postalCodeNumberOfAttempts >= MAXIMUM_NUMBER_ATTEMPTS) {
            Client.logInteractionEvent(InteractionType.PatientUnableToConfirmIdentityByPostalConfirmation);
            props.showAndUpdateErrorMessage(ErrorCodes.MaximumNumberOfAttemptsReached);
        } else {
            props.showValidationMessage(true, "invalidPostalCodeDetected");
            props.onSubmitIncrement("postalCodeNumberOfAttempts");
        }
        props.hideKeyboard();
    }

    const renderAcquiringMethod = (): JSX.Element => {
        if (props.healthCardManualEntryOnlyEnabled || props.acquiringMethod === ACQUIRING_MODE.MANUAL) {
            const isManualInputFocused = "manualHealthCardInput" === props.focusedElementId;
            return (
                <ManualInput
                    submitHealthCardNumber={(val: string) => { submitHealthCardNumberToServer(val.toUpperCase(), HealthCardAcquisitionType.ManualEntry) }}
                    invalidNumberDetected={props.invalidNumberDetected}
                    text={props.localeString}
                    countryMappedStrings={props.countryMappedStrings}
                    countryList={props.countryList}
                    changeMode={props.healthCardManualEntryOnlyEnabled ? undefined : () => { props.changeAcquiringMode(ACQUIRING_MODE.SCANNER) }}
                    stateList={props.stateList}
                    defaultCountry={props.defaultCountry}
                    defaultRegion={props.defaultRegion}
                    showKeyboard={() => { props.showKeyboard(props.onHealthCardNumberChange, props.healthCardNumber, Validator.getCountryAndRegionMask(props.currentCountry || props.defaultCountry, props.currentRegion || props.defaultRegion), "manualHealthCardInput") }}
                    onCountryOrRegionChange={(country: string, region: string) => { props.changeCountryAndRegion(country, region); }}
                    onSelectClick={() => { props.hideKeyboard() }}
                    focused={isManualInputFocused}
                    keyboardValue={props.healthCardNumber}
                    onEraseHealthCardNumber={() => { props.onHealthCardNumberChange(""); props.showKeyboard(props.onHealthCardNumberChange, "", Validator.getCountryAndRegionMask(props.currentCountry || props.defaultCountry, props.currentRegion || props.defaultRegion), "manualHealthCardInput") }}
                >
                    {props.isLoadingSpinnerActive && <LoadingSpinner />}
                </ManualInput>
            )
        }
        else if (props.acquiringMethod === ACQUIRING_MODE.SCANNER) {
            return (
                <Scanner
                    submitHealthCardNumber={(val: string) => submitHealthCardNumberToServer(val, HealthCardAcquisitionType.BarCodeScanner)}
                    text={props.localeString}
                    return={() => { props.return(); }}
                    changeMode={() => { props.changeAcquiringMode(ACQUIRING_MODE.MANUAL) }}
                    invalidNumberDetected={props.invalidNumberDetected}
                    isSubmittedToServer={props.isLoadingSpinnerActive}
                    disableScanning={props.showPostalCodeConfirmationModal}
                >
                    {props.isLoadingSpinnerActive && <LoadingSpinner />}
                </Scanner>
            );
        } else {
            return null;
        }
    }

    return (
        <div>
            <div>
                {renderAcquiringMethod()}
            </div>
            {
                props.showPostalCodeConfirmationModal &&
                <ZipCodeConfirmation
                    onSubmit={onZipCodeConfirmationSubmit}
                    onFocus={(mask: string) => {
                        props.showKeyboard(props.onPostalCodeChange, "", mask, "regionCodeConfirmation");
                    }}
                    focusedElementId={props.focusedElementId}
                    onRegionCodeChange={(regionCode: string) => { props.onPostalCodeChange(regionCode) }}
                    showModal={props.showPostalCodeConfirmationModal}
                    showErrorCode={(code) => { props.showAndUpdateErrorMessage(code) }}
                    text={props.localeString}
                    countryMappedStrings={props.countryMappedStrings}
                    isIncorrectPostalCode={props.isIncorrectPostalCode}
                    return={props.cancelPostalCodeVerification}
                    countriesListOptions={props.countriesList}
                    zipCode={props.postalCodeEntered}
                    isValid={RegionCodeValidator.validateRegionCodeByCountry(props.currentCountry, props.postalCodeEntered)}
                />
            }
        </div>
    );
}


// WEBPACK FOOTER //
// ./src/components/AcquireHealthCardNumber/AcquireHealthCardNumber.component.tsx